import { showAlertDialog, showProgressDialog } from '@juvare/common-web-ui/DialogHelper';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { handleErrorSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Mic from '@mui/icons-material/Mic';
import Stop from '@mui/icons-material/Stop';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, DialogActions, DialogContent, Fab, FormControl, Grid, IconButton, TextField, Typography, } from '@mui/material';
import React from 'react';
import { ReactMediaRecorder } from 'react-media-recorder';
import { useAlertUiTranslation } from '../../../../../hooks';
import { useAlertServiceClient } from '../../../hooks';
import { audioBufferToMp3 } from '../Utils/AudioConverter';
import { VoiceRecordingTimer } from './VoiceRecordingTimer';
export const VoiceRecordingDialog = ({ close, type, onSave, responseOptions, message }) => {
    const { t } = useAlertUiTranslation();
    const [timerRunning, setTimerRunning] = React.useState(false);
    const [recordingBlob, setRecordingBlob] = React.useState(null);
    const alertsClient = useAlertServiceClient();
    const setRecording = (type, blob, duration) => {
        showProgressDialog({
            title: t('pages.edit.voice.record.savingRecording'),
            callback: (done) => {
                alertsClient
                    .saveDocuments([new File([blob], type + '.mp3')])
                    .then((response) => {
                    var _a;
                    const newRecording = {
                        type: type,
                        id: (_a = response[0].fileId) !== null && _a !== void 0 ? _a : '',
                        duration: duration,
                    };
                    onSave(newRecording);
                    done();
                })
                    .catch((error) => {
                    done();
                    showAlertDialog({ title: t('pages.edit.validationFailed'), content: React.createElement("div", null, error) });
                });
            },
        });
    };
    const validateRecordingSize = (blob) => {
        return blob.size <= 10485760;
    };
    const validateRecordingDuration = (duration) => {
        return duration <= 600;
    };
    const getHelperTextLabel = () => {
        return type === 'responses' ? t('pages.edit.voice.record.responseOptions') : t('pages.edit.voice.record.textVoiceMessage');
    };
    const getHelperText = () => {
        return type === 'responses' ? getResponseOptionText() : getAlertMessage();
    };
    const getHelperRowCount = () => {
        if (responseOptions && type === 'responses') {
            return Math.max(responseOptions.length, 5);
        }
        else {
            return 5;
        }
    };
    const areResponseOptionsEmpty = () => {
        return responseOptions ? responseOptions.length == 0 || (responseOptions.length == 1 && !responseOptions[0].text) : true;
    };
    const getResponseOptionText = () => {
        return !areResponseOptionsEmpty()
            ? responseOptions.map((value) => `${value.text}`).join('\n')
            : t('pages.edit.voice.record.enterResponses');
    };
    const getAlertMessage = () => {
        return message || t('pages.edit.voice.record.enterTextVoice');
    };
    const onSaveRecording = (previewStream) => {
        if (!recordingBlob || !type || !validateRecordingSize(recordingBlob))
            return;
        recordingBlob
            .arrayBuffer()
            .then((recordingBuffer) => {
            const audioContext = new (window.AudioContext || window['webkitAudioContext'])();
            audioContext.decodeAudioData(recordingBuffer, function (buffer) {
                const duration = Math.floor(buffer.duration);
                if (validateRecordingDuration(duration)) {
                    const mp3Audio = audioBufferToMp3(buffer);
                    if (!validateRecordingSize(mp3Audio))
                        return;
                    setRecording(type, mp3Audio, duration);
                }
            });
        })
            .catch((error) => {
            handleErrorSnackbar(error);
            setRecording(type, recordingBlob, 0);
        });
        if (previewStream) {
            previewStream.getTracks().forEach((track) => track.stop());
        }
    };
    const initMediaRecording = (startRecording) => (React.createElement(Box, { pt: 1, pb: 3 },
        React.createElement(Grid, { container: true, spacing: 0, direction: 'column', alignItems: 'center', justifyContent: 'center' },
            React.createElement(Grid, { item: true, xs: 12 },
                React.createElement(Typography, { variant: 'subtitle1', gutterBottom: true }, t('pages.edit.voice.record.tapTheMicrophoneToBeginRecording'))),
            React.createElement(Grid, { item: true, xs: 12, sx: { py: 1 } },
                React.createElement(Fab, { color: 'primary', size: 'small', "data-testid": 'start-recording', "aria-label": 'start recording button', onClick: () => {
                        setTimerRunning(true);
                        startRecording();
                    } },
                    React.createElement(Mic, null))))));
    const mediaRecording = (stopRecording) => (React.createElement(Box, { pt: 1, pb: 3 },
        React.createElement(Grid, { container: true, spacing: 0, direction: 'column', alignItems: 'center', justifyContent: 'center' },
            React.createElement(Grid, { item: true, xs: 12 },
                React.createElement(Typography, { variant: 'subtitle1', gutterBottom: true },
                    React.createElement("strong", null,
                        t('pages.edit.voice.record.recording'),
                        ": "),
                    React.createElement(VoiceRecordingTimer, { isRunning: timerRunning }))),
            React.createElement(Grid, { item: true, xs: 12, sx: { py: 1 } },
                React.createElement(Fab, { color: 'primary', size: 'small', "data-testid": 'stop-recording', "aria-label": 'stop recording button', onClick: stopRecording },
                    React.createElement(Stop, null))))));
    const mediaStopped = (mediaBlobUrl, previewStream, clearBlobUrl) => (React.createElement(React.Fragment, null,
        React.createElement(Grid, { container: true, spacing: 0, direction: 'column', alignItems: 'center', justifyContent: 'center', sx: { px: 3 } },
            React.createElement(Grid, { item: true, xs: 12, sx: { width: '100%' } },
                React.createElement("audio", { "data-testid": 'audio-recording', preload: 'auto', style: { width: '100%' }, src: mediaBlobUrl, controls: true, controlsList: 'nodownload' }))),
        React.createElement(DialogActions, null,
            React.createElement(Button, { "data-test": 'record-again-button', color: 'primary', "aria-label": 'record again button', onClick: clearBlobUrl }, t('pages.edit.voice.record.recordAgain')),
            React.createElement(Button, { "data-testid": 'save-recording-button', "aria-label": 'save recording button', color: 'primary', onClick: () => {
                    onSaveRecording(previewStream);
                    close();
                } }, t('pages.edit.voice.record.save')))));
    const renderRecorder = ({ error, status, startRecording, stopRecording, mediaBlobUrl, clearBlobUrl, previewStream, }) => {
        if (error) {
            return (React.createElement(Box, { pb: 3 },
                React.createElement(Alert, { severity: 'error' }, t('pages.edit.voice.record.unableToAccessMicrophone'))));
        }
        if (status === 'stopped' && mediaBlobUrl) {
            return mediaStopped(mediaBlobUrl, previewStream, clearBlobUrl);
        }
        else if (status === 'recording') {
            return mediaRecording(stopRecording);
        }
        else if (status === 'acquiring_media') {
            return (React.createElement(Box, { pb: 3 },
                React.createElement(Alert, { severity: 'warning' }, t('pages.edit.voice.record.microphoneIsNotActive'))));
        }
        else {
            return initMediaRecording(startRecording);
        }
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(DialogContent, null,
            React.createElement(IconButton, { edge: 'end', color: 'inherit', "data-testid": 'close-button', "aria-label": 'close recording dialog button', onClick: () => close(), sx: { position: 'absolute', right: 16, top: 16 } },
                React.createElement(CloseIcon, null)),
            React.createElement(Typography, { variant: 'h6' }, t('pages.edit.voice.record.title')),
            React.createElement(Typography, { variant: 'subtitle1', sx: { color: themeColors.text.subtle } }, t('pages.edit.voice.record.message'))),
        React.createElement(DialogContent, { sx: { display: 'block', justifyContent: 'center' } },
            React.createElement(FormControl, { fullWidth: true, margin: 'dense' },
                React.createElement(TextField, { label: getHelperTextLabel(), type: 'text', color: 'primary', variant: 'outlined', multiline: true, rows: getHelperRowCount(), value: getHelperText(), inputProps: {
                        readOnly: true,
                        'data-testid': 'helper-text',
                        'aria-label': 'helper text field',
                    } })),
            type === 'responses' && (React.createElement(Accordion, null,
                React.createElement(AccordionSummary, { expandIcon: React.createElement(ExpandMoreIcon, null), "data-testid": 'responses-replay-reminder', sx: {
                        backgroundColor: 'rgba(0, 0, 0, .03)',
                    } },
                    React.createElement(Typography, null, t('pages.edit.voice.record.replayMessageReminder'))),
                React.createElement(AccordionDetails, null,
                    React.createElement(Box, null,
                        React.createElement(Typography, { variant: 'body2', gutterBottom: true },
                            "\"",
                            t('pages.edit.voice.record.replayMessageReminderText'),
                            "\""),
                        React.createElement(Typography, { variant: 'caption' }, t('pages.edit.voice.record.replayMessageReminderEnd'))))))),
        React.createElement(ReactMediaRecorder, { audio: true, onStop: (blobUrl, blob) => {
                setRecordingBlob(blob);
            }, render: renderRecorder })));
};
